// Poll-delta detection (PURE, dependency-free) for the "watch it think" surprise.
// Spec §5 / Bible §5. We gently re-poll EXISTING read endpoints and brighten a node
// ONLY when a genuine change is detected between snapshots.
//
// HONESTY (absolute): a surprise fires ONLY on a real delta. The first snapshot yields
// NO deltas (awaken handles first load). No simulated/timed/random events — this module
// compares two real payloads and returns exactly what changed, or nothing.

// Reduce fetched read-payloads to a comparable snapshot.
export function snapshotSignals({ outcomes = [], observations = [], evaluations = [] } = {}) {
  const outcomeVersion = {};   // subject_ref_id -> max version
  const outcomeLatestId = {};  // subject_ref_id -> outcome_id at max version
  const outcomeIds = [];
  for (const o of outcomes) {
    if (o.outcome_id) outcomeIds.push(o.outcome_id);
    const s = o.subject_ref_id;
    if (s == null) continue;
    const v = o.version || 0;
    if (outcomeVersion[s] == null || v > outcomeVersion[s]) {
      outcomeVersion[s] = v;
      outcomeLatestId[s] = o.outcome_id;
    }
  }
  const observationIds = observations.map((o) => o.observation_id || o.id).filter(Boolean);
  const evalVersion = {}; // cohort_key -> evidence_version
  for (const e of evaluations) {
    if (e.cohort_key) evalVersion[e.cohort_key] = e.evidence_version || null;
  }
  return { outcomeVersion, outcomeLatestId, outcomeIds, observationIds, evalVersion };
}

// Compare two snapshots. Returns { first, deltas:[{kind, refId, nodeId, subject?}] }.
// nodeId is the graph node to brighten, or null for cohort-level changes (announce only).
export function detectPollDeltas(prev, next) {
  if (!prev) return { first: true, deltas: [] };
  const deltas = [];

  const prevOutcomeIds = new Set(prev.outcomeIds || []);
  for (const id of next.outcomeIds || []) {
    if (!prevOutcomeIds.has(id)) {
      // a brand-new outcome id we hadn't seen
      deltas.push({ kind: "NEW_OUTCOME", refId: id, nodeId: `outcome:${id}` });
    }
  }

  for (const subject of Object.keys(next.outcomeVersion || {})) {
    const before = (prev.outcomeVersion || {})[subject];
    const after = next.outcomeVersion[subject];
    if (before != null && after > before) {
      const latest = (next.outcomeLatestId || {})[subject];
      deltas.push({ kind: "OUTCOME_VERSION_UP", refId: latest || subject, subject, nodeId: latest ? `outcome:${latest}` : null });
    }
  }

  const prevObs = new Set(prev.observationIds || []);
  for (const id of next.observationIds || []) {
    if (!prevObs.has(id)) deltas.push({ kind: "NEW_OBSERVATION", refId: id, nodeId: `observation:${id}` });
  }

  for (const cohort of Object.keys(next.evalVersion || {})) {
    const before = (prev.evalVersion || {})[cohort];
    const after = next.evalVersion[cohort];
    if (before !== undefined && before !== after) {
      // cohort/producer-level change — no single node to brighten (announce only).
      deltas.push({ kind: "EVAL_CHANGED", refId: cohort, nodeId: null });
    }
  }

  return { first: false, deltas };
}

// Dedup helper: node ids to brighten for a set of deltas (drops null nodeIds).
export function brightenTargets(deltas) {
  return [...new Set((deltas || []).map((d) => d.nodeId).filter(Boolean))];
}
